// Die Unterlage für den Monteur (E3, S4; H17 – Manuel, 21.10.: Fotos mit
// Zeichnung, Notizen, Längen, Material; PLAN.md, E3, „Handwerk“: zuerst als
// Druckansicht im Browser; Mockup /crm/pdf/:id). Eine Seite im CRM wie ein
// A4-Blatt; „Drucken / als PDF sichern“ macht der Browser. Seit dem
// Augentest E3b (Gernot, 23.10.2026) daneben „PDF herunterladen“: dasselbe
// Blatt als PDF, hier im Browser gebaut, Fotos als JPEG – klein genug für
// eine Mail, mit dem richtigen Namen auch am iPhone (unterlage-pdf.ts). Ein
// PDF, das der Server selbst verschickt, kommt mit der Kiste Montage.
//
// Was auf dem Blatt steht: Kopf mit Logo; Kunde, Adresse, Telefon, Objekt,
// Anlage, aufgenommen und freigegeben (wann, von wem), Montagetermin, die
// Dachplanung; offen gebliebene Stationen deutlich; je Station die Fotos mit
// der Zeichnung darüber (SVG, ganz, nichts abgeschnitten), ihre Notizen, die
// Notizen zur Station, die Längen, „Sonstiges“; am Ende das Material, das
// jetzt gilt. Unten Firma und „Seite x von y“ (druck.ts). Seit E3b (O65) je
// Station das Material von draußen und „Material am Schluss“; Längen nur
// noch bei Besichtigungen, die welche haben.
//
// Gedruckt wird erst, wenn alle Fotos da sind: Der Knopf wartet auf jedes
// Bild („Fotos laden … 3 von 12“) – sonst fehlten auf dem Papier Fotos, die
// am Bildschirm gleich gekommen wären.
//
// Adresse: #/unterlage/<kennung der Besichtigung>

import { useCallback, useEffect, useState } from "react";
import { PRODUKTLINIEN, type CrmAufnahme } from "@happy-energy/kern";
import { fehlerMelden, Ikon, Spaeter, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { adresseZeigen, dateiAdresse, objektArtName, zahlZeigen, zeitpunktZeigen } from "../anzeige.ts";
import { seitenStil } from "../druck.ts";
import { ZeichenEbene } from "../freigabe/fotos.tsx";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { mengeZeigen, woherText } from "../material.ts";
import { dateiRahmen } from "../freigabe/DachplanungKarte.tsx";
import { unterlageDateiname } from "../freigabe/dachplanung.ts";
import { useDruckTitel } from "../drucktitel.ts";
import { druckfassungRechnen, type DruckWerkzeug } from "./druckfassung.ts";
import { nacheinander, PDF_IM_BROWSER, pdfHerunterladen } from "./pdf-browser.ts";
import { geheZu, Seite } from "../rahmen.tsx";
import { useCrm } from "../zusammenhang.tsx";
import {
  draussenAufDemBlatt,
  fotosJeZeile,
  materialAufDemBlatt,
  montageterminText,
  pdfFehlerFuerWache,
  pdfFertigMeldung,
  stationenAufDemBlatt,
  type MaterialAufDemBlatt,
  type StationAufDemBlatt,
} from "./unterlage.ts";
import "./unterlage.css";

type Foto = CrmAufnahme["fotos"][number];

// --- Fotos: geladen, bevor gedruckt wird -------------------------------------------------------------

/** Je Foto: geladen (true) oder lässt sich nicht laden (false); fehlt: lädt noch. */
type FotoStand = ReadonlyMap<string, boolean>;

function useFotoStand(): { stand: FotoStand; melden: (id: string, ok: boolean) => void } {
  const [stand, setStand] = useState<FotoStand>(new Map());
  const melden = useCallback((id: string, ok: boolean) => {
    setStand((alt) => (alt.get(id) === ok ? alt : new Map(alt).set(id, ok)));
  }, []);
  return { stand, melden };
}

/** Bild und Leinwand aus dem Browser für die Druckfassung (druckfassung.ts). */
const IM_BROWSER: DruckWerkzeug<ImageBitmap> = {
  bildAus: (roh) => createImageBitmap(roh),
  leinwand: () => document.createElement("canvas"),
};


/**
 * Seit E3b (O77): eine kleinere Fassung des Fotos fürs Blatt (druckfassung.ts).
 * Geholt wird jedes Foto gleich, umgerechnet nacheinander. Geht es nicht
 * (alter Browser, Speicher), gilt das Original – nie ein fehlendes Foto. Ist
 * die Seite schon verlassen, wird nicht mehr gerechnet und nichts angelegt.
 */
function useDruckfassung(pruefsumme: string): string | null {
  const original = dateiAdresse(pruefsumme);
  const [adresse, setAdresse] = useState<string | null>(null);
  useEffect(() => {
    let weg = false;
    let eigene: string | null = null;
    void (async () => {
      try {
        if (typeof createImageBitmap !== "function") throw new Error("kein createImageBitmap");
        const antwort = await fetch(original, { credentials: "same-origin" });
        if (!antwort.ok) throw new Error(String(antwort.status));
        const roh = await antwort.blob();
        const klein = await nacheinander(() => (weg ? Promise.resolve(null) : druckfassungRechnen(roh, IM_BROWSER)));
        if (weg) return;
        if (!klein) throw new Error("das Original gilt");
        eigene = URL.createObjectURL(klein);
        setAdresse(eigene);
      } catch {
        if (!weg) setAdresse(original);
      }
    })();
    return () => {
      weg = true;
      if (eigene) URL.revokeObjectURL(eigene);
    };
  }, [original]);
  return adresse;
}

// --- PDF herunterladen (seit dem Augentest E3b) ---------------------------------------------------

// Was der Browser dafür liefert (Fotos, Logo) und das Sichern der Datei: pdf-browser.ts (seit E3c, S4 – die
// Dach-Unterlage braucht dasselbe).

/** „PDF herunterladen“: baut das PDF (die Bibliothek kommt erst beim Klick), zeigt, wie weit, und sichert es. */
function PdfKnopf({ a, firma, name }: { a: CrmAufnahme; firma: string; name: string }) {
  const [stand, setStand] = useState<{ fertig: number; gesamt: number } | null>(null);

  async function bauen() {
    setStand({ fertig: 0, gesamt: a.fotos.length });
    try {
      const { unterlagePdfBauen } = await import("./unterlage-pdf.ts");
      const { pdf, fotosFehlen } = await unterlagePdfBauen({ a, firma, titel: name }, PDF_IM_BROWSER, (fertig, gesamt) => setStand({ fertig, gesamt }));
      pdfHerunterladen(pdf, `${name}.pdf`);
      const fertig = pdfFertigMeldung(`${name}.pdf`, fotosFehlen);
      toastZeigen(fertig.text, fertig.art);
    } catch (fehler) {
      fehlerMelden("crm", pdfFehlerFuerWache(fehler));
      toastZeigen("Das PDF ließ sich nicht bauen – bitte noch einmal versuchen, sonst „Drucken / als PDF sichern“.", "fehler");
    } finally {
      setStand(null);
    }
  }

  return (
    <button type="button" className="crm-knopf dunkel klein" onClick={() => void bauen()} disabled={stand !== null}>
      {stand ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="herunterladen" groesse="k" />}
      {stand ? `PDF wird gebaut … ${stand.fertig} von ${stand.gesamt}` : "PDF herunterladen"}
    </button>
  );
}

/** Ein Foto auf dem Blatt: ganz (eingepasst), die Zeichnung genauso eingepasst darüber. */
function BlattFoto({ foto, melden, kaputt }: { foto: Foto; melden: (id: string, ok: boolean) => void; kaputt: boolean }) {
  // Kam das Bild schon aus dem Zwischenspeicher, ist es fertig, bevor React zuhört – dann gleich melden.
  const schonDa = useCallback(
    (img: HTMLImageElement | null) => {
      if (img?.complete) melden(foto.id, img.naturalWidth > 0);
    },
    [foto.id, melden],
  );
  const adresse = useDruckfassung(foto.pruefsumme);
  return (
    <div className="blatt-foto">
      {kaputt ? (
        <span className="blatt-foto-fehlt">
          <Ikon name="achtung" /> Foto lässt sich nicht laden
        </span>
      ) : adresse === null ? null : (
        <>
          <img ref={schonDa} src={adresse} alt="" loading="eager" onLoad={() => melden(foto.id, true)} onError={() => melden(foto.id, false)} />
          <ZeichenEbene zeichnung={foto.zeichnung} breite={foto.breite} hoehe={foto.hoehe} decken={false} einpassen />
        </>
      )}
    </div>
  );
}

// --- Das Blatt -------------------------------------------------------------------------------------

/** Das Material von draußen auf dem Blatt – wie die Längen: links was, rechts wie viel. */
function DraussenListe({ material }: { material: readonly MaterialAufDemBlatt[] }) {
  return (
    <dl className="blatt-laengen">
      {material.map((m, i) => (
        <div key={i}>
          <dt>
            {m.bezeichnung}
            {m.artikelnummer && <span className="blatt-unter">{m.artikelnummer}</span>}
          </dt>
          <dd>
            <b>
              {zahlZeigen(m.menge)} {m.einheit}
            </b>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Station({
  s,
  stand,
  melden,
  mitLaengen,
}: {
  s: StationAufDemBlatt;
  stand: FotoStand;
  melden: (id: string, ok: boolean) => void;
  /** Längen gibt es nur bei Besichtigungen von vor E3b – sonst steht dort das Material von draußen. */
  mitLaengen: boolean;
}) {
  return (
    <section className={`blatt-station${s.fertig ? "" : " offen"}`} aria-label={`Station ${s.nr}: ${s.name}`}>
      <h3>
        <span className="blatt-nr">{s.nr}</span> {s.name}
        {!s.fertig && <span className="blatt-offen">nicht fertig – hier fehlt vielleicht etwas</span>}
      </h3>
      {s.fotos.length > 0 ? (
        <div className={`blatt-fotos je-${fotosJeZeile(s.fotos.length)}`}>
          {s.fotos.map((f, i) => (
            <figure key={f.foto.id} className="blatt-figur">
              <BlattFoto foto={f.foto} melden={melden} kaputt={stand.get(f.foto.id) === false} />
              <figcaption>
                <b>Foto {i + 1}</b>
                {f.notizen.length > 0 && ` · ${f.notizen.join(" · ")}`}
                {f.memos > 0 && <span className="blatt-memo"> · {f.memos === 1 ? "dazu ein Sprachmemo" : `dazu ${f.memos} Sprachmemos`} (im CRM anhören)</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      ) : (
        <p className="blatt-leer">Keine Fotos an dieser Station.</p>
      )}
      <div className="blatt-station-text">
        <div>
          <h4>Notizen</h4>
          {s.notizen.length === 0 && <p className="blatt-leer">Keine Notiz.</p>}
          {s.notizen.map((n, i) => (
            <p key={i} className="blatt-notiz">
              {n.text ?? "Nur eingesprochen – das Sprachmemo im CRM anhören."}
              {n.text !== null && n.memos > 0 && <span className="blatt-memo"> (dazu ein Sprachmemo im CRM)</span>}
            </p>
          ))}
          {s.sonstiges && (
            <>
              <h4>Sonstiges</h4>
              <p className="blatt-notiz">{s.sonstiges}</p>
            </>
          )}
        </div>
        <div>
          {s.material.length > 0 && (
            <>
              <h4>Material von draußen</h4>
              <DraussenListe material={s.material} />
            </>
          )}
          {!mitLaengen && s.material.length === 0 && (
            <>
              <h4>Material von draußen</h4>
              <p className="blatt-leer">Keines angegeben.</p>
            </>
          )}
          {mitLaengen && <h4>Längen</h4>}
          {!mitLaengen ? null : s.laengen.length === 0 ? (
            <p className="blatt-leer">Keine Längen.</p>
          ) : (
            <dl className="blatt-laengen">
              {s.laengen.map((l) => (
                <div key={l.name}>
                  <dt>{l.name}</dt>
                  <dd>
                    <b>
                      {zahlZeigen(l.summe)} {l.einheit}
                    </b>
                    {l.werte.length > 1 && <span className="blatt-teile"> ({l.werte.map((w) => `${zahlZeigen(w.meter)} ${l.einheit}`).join(" + ")})</span>}
                    {l.werte
                      .filter((w) => w.anmerkung)
                      .map((w, i) => (
                        <span key={i} className="blatt-anmerkung">
                          {zahlZeigen(w.meter)} {l.einheit}: {w.anmerkung}
                        </span>
                      ))}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </section>
  );
}

function Blatt({ a, stand, melden }: { a: CrmAufnahme; stand: FotoStand; melden: (id: string, ok: boolean) => void }) {
  const { ich } = useCrm();
  useDruckTitel(unterlageDateiname(a.kunde.name, dateiRahmen(a.begonnenAm, a.anlage.produktlinie), a.stand !== "freigegeben"));
  const stationen = stationenAufDemBlatt(a);
  const offen = stationen.filter((s) => !s.fertig);
  const freigegeben = a.stand === "freigegeben";
  const material = materialAufDemBlatt(a.positionen, a.freigegebenAm);
  const amSchluss = draussenAufDemBlatt(a, null);
  const produkt = PRODUKTLINIEN.find((p) => p.schluessel === a.anlage.produktlinie)?.name ?? a.anlage.produktlinie;
  const k2 = a.anlage.k2;
  const dachplanung = k2.pruefsumme !== null ? "K2-PDF liegt im CRM – bitte dazu drucken" : k2.uebersprungen ? "übersprungen" : "noch keine";

  return (
    <article className="a4 unterlage-blatt" aria-label={`Unterlage für die Montage – ${a.kunde.name}`}>
      <header className="a4-kopf">
        <img src="/logo-horizontal.png" alt="Happy Energy" />
        <div className="a4-kopf-wort">
          <b>Aufnahme vor Ort</b>
          <span>Unterlage für die Montage</span>
          <span className="a4-firma">{ich.firma.name}</span>
        </div>
      </header>
      {/* Gernot braucht die Unterlage oft schon vor der Freigabe – dann soll sie „ganz dick und fett“ sagen, dass sie ein Entwurf ist (23.10.2026). */}
      {!freigegeben && (
        <p className="a4-stempel">
          <b>Entwurf – noch nicht freigegeben.</b> <span>Das Material hat das Büro noch nicht geprüft.</span>
        </p>
      )}
      <h2>{a.kunde.name}</h2>
      <dl className="a4-felder">
        <div>
          <dt>Adresse</dt>
          <dd>{adresseZeigen(a.objekt)}</dd>
        </div>
        <div>
          <dt>Telefon</dt>
          <dd>{a.kunde.telefon ?? "–"}</dd>
        </div>
        <div>
          <dt>Objekt</dt>
          <dd>{objektArtName(a.objekt.art)}</dd>
        </div>
        <div>
          <dt>Anlage</dt>
          <dd>{produkt}</dd>
        </div>
        <div>
          <dt>Aufgenommen</dt>
          <dd>
            {zeitpunktZeigen(a.begonnenAm)} · {a.verkaeufer.name}
          </dd>
        </div>
        <div>
          <dt>Freigegeben</dt>
          <dd>{a.freigegebenAm ? `${zeitpunktZeigen(a.freigegebenAm)}${a.freigegebenVon ? ` · ${a.freigegebenVon.name}` : ""}` : "noch nicht"}</dd>
        </div>
        <div className="a4-termin">
          <dt>Montagetermin</dt>
          <dd>{montageterminText(a.anlage.montagetermin)}</dd>
        </div>
        <div>
          <dt>Dachplanung (K2)</dt>
          <dd>{dachplanung}</dd>
        </div>
      </dl>
      {offen.length > 0 && (
        <p className="a4-warnung">
          <Ikon name="achtung" groesse="k" />
          <span>
            <b>Nicht als fertig markiert:</b> {offen.map((s) => `Station ${s.nr} (${s.name})`).join(", ")} – dort fehlt vielleicht etwas. Bitte vor Ort
            prüfen.
          </span>
        </p>
      )}

      {stationen.map((s) => (
        <Station key={s.id} s={s} stand={stand} melden={melden} mitLaengen={a.laengen.length > 0} />
      ))}

      {amSchluss.length > 0 && (
        <section className="blatt-station" aria-label="Material am Schluss">
          <h3>Material am Schluss – vom Verkauf angegeben</h3>
          <DraussenListe material={amSchluss} />
        </section>
      )}

      <section className="blatt-material" aria-label="Material">
        <h3>Material{freigegeben ? " – freigegeben" : ""}</h3>
        {freigegeben ? (
          <>
            <p className="blatt-stand">
              Stand: {material.geaendertAm ? `zuletzt geändert ${zeitpunktZeigen(material.geaendertAm)}` : `wie freigegeben ${a.freigegebenAm ? zeitpunktZeigen(a.freigegebenAm) : ""}`}
            </p>
            {material.geltende.length === 0 ? (
              <p className="blatt-leer">In der Liste steht kein Material.</p>
            ) : (
              <table className="blatt-tabelle">
                <thead>
                  <tr>
                    <th scope="col" className="zahl">
                      Menge
                    </th>
                    <th scope="col">Einheit</th>
                    <th scope="col">Material</th>
                  </tr>
                </thead>
                <tbody>
                  {material.geltende.map((p) => {
                    const woher = woherText(p, a.stationen);
                    return (
                      <tr key={p.id}>
                        <td className="zahl">{mengeZeigen(p.menge)}</td>
                        <td>{p.einheit}</td>
                        <td>
                          {p.bezeichnung}
                          {(p.artikelnummer || woher) && (
                            <span className="blatt-unter">{[p.artikelnummer, woher && p.stationId !== null ? `aus ${woher}` : woher].filter(Boolean).join(" · ")}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        ) : (
          <p className="blatt-leer">Noch nicht freigegeben – das Material steht hier, sobald das Büro freigibt.</p>
        )}
      </section>

      <footer className="a4-fuss">
        <span>{ich.firma.name} · Happy Energy</span>
        <span>Unterlage für die Montage · {a.kunde.name}</span>
      </footer>
    </article>
  );
}

// --- Die Seite -------------------------------------------------------------------------------------

export function UnterlageSeite({ id }: { id: string }) {
  const { ich } = useCrm();
  const { daten: a, fehler, neu } = useLaden<CrmAufnahme>(`/api/crm/aufnahme/${id}`);
  const { stand, melden } = useFotoStand();
  const [trotzdem, setTrotzdem] = useState(false);
  const fotos = a?.fotos ?? [];
  const fertig = fotos.filter((f) => stand.has(f.id)).length;
  const kaputt = fotos.filter((f) => stand.get(f.id) === false).length;
  const bereit = a !== undefined && (fertig === fotos.length || trotzdem);

  function zurueck() {
    // Zurück dorthin, woher man kam (Unterlagen, Akte) – ein neuer Tab kennt keinen Weg zurück.
    if (window.history.length > 1) window.history.back();
    else geheZu("/unterlagen");
  }

  return (
    <Seite
      titel={a ? `Unterlage · ${a.kunde.name}` : "Unterlage für die Montage"}
      unter="Für die Montage – als PDF herunterladen oder drucken"
      knoepfe={
        <>
          <button type="button" className="crm-knopf rand klein" onClick={zurueck}>
            <Ikon name="links" groesse="k" /> Zurück
          </button>
          {a && <PdfKnopf a={a} firma={ich.firma.name} name={unterlageDateiname(a.kunde.name, dateiRahmen(a.begonnenAm, a.anlage.produktlinie), a.stand !== "freigegeben")} />}
          {a && (
            <button type="button" className="crm-knopf rand klein" onClick={() => window.print()} disabled={!bereit}>
              {bereit ? <Ikon name="drucker" groesse="k" /> : <span className="kreisel" aria-hidden="true" />}
              {bereit ? "Drucken / als PDF sichern" : `Fotos laden … ${fertig} von ${fotos.length}`}
            </button>
          )}
          {a && !bereit && (
            <button type="button" className="crm-knopf leise klein" onClick={() => setTrotzdem(true)}>
              trotzdem drucken
            </button>
          )}
        </>
      }
    >
      <style>{seitenStil(`${ich.firma.name} · Unterlage für die Montage`)}</style>
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!a && fehler === undefined && <Laedt text="Die Unterlage wird geladen …" />}
      {a && (
        <div className="unterlage-seite">
          {a.stand !== "freigegeben" && (
            <Tipp art="warn" ikon="achtung">
              Diese Besichtigung ist noch nicht freigegeben – die Unterlage ist erst mit der Freigabe fertig (dann steht auch das Material darauf).
            </Tipp>
          )}
          {kaputt > 0 && (
            <Tipp art="rot" ikon="achtung">
              {kaputt === 1 ? "Ein Foto lässt" : `${kaputt} Fotos lassen`} sich gerade nicht laden – auf dem Ausdruck fehlt es. Verbindung prüfen und die Seite neu laden.
            </Tipp>
          )}
          <Blatt a={a} stand={stand} melden={melden} />
          <div className="unterlage-spaeter">
            <Spaeter titel="Schritt-für-Schritt-Unterlage in der Montage-App" wann="Kiste Montage">
              Die Partie sieht ihre Baustellen, die Unterlage Schritt für Schritt am Handy und meldet „fertig“ an die Inbetriebnahme.
            </Spaeter>
            <Spaeter titel="PDF, das der Server selbst verschickt oder ablegt" wann="Kiste Montage">
              Die Unterlage geht von selbst an die Partie oder ins Archiv – kommt, wenn klar ist, wer sie wohin bekommt. Bis dahin: „PDF herunterladen“.
            </Spaeter>
          </div>
        </div>
      )}
    </Seite>
  );
}
